import '../global-sass/main.scss';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { usePrefs, useRequestData } from 'wsm-common-data';
import { isBrowser } from 'ws-scripts/modules/environment';
import Parser from 'html-react-parser';
import MenuItem from '../components/MenuItem';
import MobileFullscreenSubnav from '../components/MobileFullscreenSubnav';
import { getNavClasses } from '../utils/getNavClasses';
import { getNavAttributes } from '../utils/getNavAttributes';
import {
	MENU_TYPE_MEGA_MENU,
	MENU_TYPE_MOBILE,
	MENU_TYPE_DEFAULT
} from '../constants';
import { setUri } from '../actions/currentPage';

const Widget = () => {
	const navItems = useSelector((state) => state.items);
	const navsV2 = useSelector((state) => state.widgetData.navsV2);
	const fullscreenMobileSubnavOpen = useSelector(
		(state) => state.menu.fullscreenMobileSubnavOpen
	);
	const {
		dropdownDisplayType,
		enableMegaMenus,
		enableSiteWideSearch,
		enableTwoLineLabelling,
		mobileNavClass,
		mobileSubnavType,
		navClasses,
		navListClasses,
		widgetClasses,
		showFooterLinksIcons,
		showCurtain,
		sticky,
		'navigation.id': navId
	} = usePrefs();

	if (typeof window !== 'undefined') {
		window.DDC = window.DDC || {};
		window.DDC.EventFired = window.DDC.EventFired || {};
	}

	const dispatch = useDispatch();
	const siteWidePositionFirst = enableSiteWideSearch === 'first';
	const siteWidePositionLast = enableSiteWideSearch === 'last';
	const { composer, deviceType } = useRequestData();
	const [isSiteTextSearch, setIsSiteTextSearch] = useState(false);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const { pathname } = window?.location;
			dispatch(setUri(pathname));
		}
	}, [dispatch]);

	useEffect(() => {
		let count = 0;
		const checkForWsSiteTextSearch = () => {
			count += 1;
			if (
				window?.DDC?.EventFired['ws-sts-navigation-registered'] &&
				deviceType === 'DESKTOP'
			) {
				setIsSiteTextSearch(true);
			} else if (count < 15) {
				setTimeout(checkForWsSiteTextSearch, 100);
			}
		};
		checkForWsSiteTextSearch();
	}, [deviceType]);

	// matchHeight() must fire once, as early as possible
	useEffect(() => {
		function matchHeight(init) {
			$('.dropdown.dropdown').each(function matchingHeightCallback() {
				const $menuPanel = $(this);
				const $navParent = $menuPanel.closest('.ws-navigation');
				const offsetHeight =
					$navParent?.length > 0
						? $navParent[0]?.offsetHeight || 50
						: 50;

				if ($menuPanel.hasClass('open') || init) {
					const $item = $menuPanel.find('.dropdown-menu');

					$item.css({
						maxHeight: '70vh',
						overflowY: 'auto',
						top: `${offsetHeight}px`,
						position: 'absolute'
					});
				}
			});
		}

		if (
			enableMegaMenus === 'true' &&
			isBrowser &&
			typeof window.jQuery === 'function'
		) {
			// Add a small delay to ensure DOM is ready
			setTimeout(() => {
				matchHeight(true);
			}, 100);
		}
	});

	const getNavClassesAndAttr = () => {
		let menuType;
		switch (true) {
			case deviceType === 'MOBILE':
				menuType = MENU_TYPE_MOBILE;
				break;
			case enableMegaMenus === 'true':
				menuType = MENU_TYPE_MEGA_MENU;
				break;
			default:
				menuType = MENU_TYPE_DEFAULT;
		}

		const classesToReturn = getNavClasses(menuType, {
			widgetClasses,
			sticky,
			enableTwoLineLabelling,
			mobileNavClass,
			navListClasses,
			navsV2,
			navItems
		});

		const attributesToReturn = getNavAttributes(menuType, {
			dropdownDisplayType,
			composer,
			navId,
			showCurtain,
			mobileNavClass
		});

		return [classesToReturn.join(' '), attributesToReturn];
	};

	// universal attributes and classes for the nav (react-bootstrap turns this into a <ul>)
	const [classes, attributes] = getNavClassesAndAttr();

	const MenuWrapper = (Component) => {
		if (deviceType === 'MOBILE') {
			if (
				mobileSubnavType === 'full-screen' &&
				fullscreenMobileSubnavOpen
			) {
				return (
					<nav>
						<MobileFullscreenSubnav />
					</nav>
				);
			} else {
				return <nav>{Component}</nav>;
			}
		} else if (enableMegaMenus === 'true') {
			return Component;
		} else {
			const nonMMWrapperAttributes = {};
			const nonMMWrapperClasses = `non-mega-menu ${
				navClasses.toLowerCase() === 'blank' ? '' : `${navClasses}`
			}`;

			if (composer) {
				nonMMWrapperAttributes.navid = navId;
			}

			if (showFooterLinksIcons === 'true') {
				nonMMWrapperAttributes['data-show-footer-links-icons'] = '';
			}

			return (
				<nav
					className={nonMMWrapperClasses}
					{...nonMMWrapperAttributes}
				>
					{Component}
				</nav>
			);
		}
	};

	return MenuWrapper(
		<ul className={classes} {...attributes}>
			{siteWidePositionFirst && isSiteTextSearch && (
				<MenuItem
					isSiteWideSearchNavItem={isSiteTextSearch}
					siteWidePositionFirst
					siteWidePositionLast={false}
					searchPosition="first"
				/>
			)}
			{navItems.length > 0 &&
				navItems
					.filter((navItem) => navItem?.state !== 'disabled')
					.map((item, index) => {
						const {
							columns,
							id,
							label,
							layout,
							navigationItems,
							pageId,
							target,
							url
						} = item;
						const labelValue = Parser(label);
						const menuItemKey = `${id}-${index}`;
						return (
							<MenuItem
								columns={columns}
								deviceType={deviceType}
								id={id}
								index={index}
								isSiteWideSearchNavItem={false}
								key={menuItemKey}
								label={labelValue}
								layout={layout}
								numTopLevelItems={navItems.length}
								pageId={pageId}
								siteWidePositionFirst={siteWidePositionFirst}
								siteWidePositionLast={siteWidePositionLast}
								subnavItems={navigationItems}
								target={target}
								url={url}
							/>
						);
					})}
			{siteWidePositionLast && isSiteTextSearch && (
				<MenuItem
					isSiteWideSearchNavItem={isSiteTextSearch}
					siteWidePositionFirst={false}
					siteWidePositionLast
					searchPosition="last"
				/>
			)}
		</ul>
	);
};

export default Widget;
